import type { BlockWireframe } from '@octopus-do/octopus-project-core'
import type { WireframeGroup } from '@octopus-do/octopus-storybook'

// A wireframe of the catalog: `tags` are what the search matches besides the name.
interface WireframeEntry {
  id: BlockWireframe
  name: string
  tags: string[]
}

interface WireframeSection {
  id: string
  items: WireframeEntry[]
  title: string
}

const CONTENT: WireframeSection = {
  id: 'content',
  items: [
    {
      id: 'wide',
      name: 'Image',
      tags: ['image', 'picture', 'photograph', 'illustration'],
    },
    { id: 'text', name: 'Text', tags: ['text', 'paragraph'] },
    { id: 'hero_with_arrows', name: 'Slider', tags: ['slider', 'carousel'] },
    {
      id: 'double',
      name: 'Images',
      tags: [
        'images',
        'pictures',
        'photographs',
        'illustrations',
        'columns',
        '2',
      ],
    },
    { id: 'text_double', name: 'Text', tags: ['text', 'columns', '2'] },
    { id: 'slider', name: 'Slider', tags: ['slider', 'carousel'] },
    {
      id: 'triple',
      name: 'Images',
      tags: [
        'images',
        'pictures',
        'photographs',
        'illustrations',
        'columns',
        '3',
      ],
    },
    { id: 'text_triple', name: 'Text', tags: ['text', 'columns', '3'] },
    {
      id: 'text_on_image',
      name: 'Text on image',
      tags: ['text on image', 'image'],
    },
    {
      id: 'logos',
      name: 'Images',
      tags: [
        'images',
        'pictures',
        'photographs',
        'illustrations',
        'clients',
        'logos',
        'columns',
        '4',
      ],
    },
    {
      id: 'text_quarter',
      name: 'Text',
      tags: ['text', 'paragraph', 'columns', '4'],
    },
    { id: 'table_2', name: 'Table', tags: ['table', 'spreadsheet'] },
    { id: 'two_column_slider', name: 'Carousel', tags: ['carousel', 'slider'] },
    { id: 'articles_3', name: 'Articles', tags: ['articles', 'columns', '2'] },
    { id: 'table', name: 'Table', tags: ['table'] },
    { id: 'carousel', name: 'Carousel', tags: ['carousel', 'slider'] },
    { id: 'articles', name: 'Articles', tags: ['articles', 'columns', '3'] },
    { id: 'table_row', name: 'Table row', tags: ['table row'] },
    { id: 'image', name: 'Text & Image', tags: ['text & image', 'image'] },
    { id: 'articles_2', name: 'Articles', tags: ['articles', 'columns', '2'] },
    {
      id: 'image_right',
      name: 'Text & Image',
      tags: ['text & image', 'image'],
    },
    { id: 'cards_left', name: 'Cards left', tags: ['cards', 'cards left'] },
    { id: 'cards', name: 'Cards', tags: ['cards', 'tiles', 'bento'] },
    { id: 'cards_right', name: 'Cards right', tags: ['cards', 'cards right'] },
    {
      id: 'catalog',
      name: 'Catalog & filter',
      tags: ['catalog', 'catalog & filter'],
    },
    { id: 'catalog_2', name: 'Catalog', tags: ['catalog'] },
    { id: 'post_thread', name: 'Post', tags: ['post', 'thread'] },
    { id: 'text_and_video', name: 'Video left', tags: ['video left', 'text'] },
    { id: 'video', name: 'Video', tags: ['video', 'youtube'] },
    {
      id: 'text_and_video_2',
      name: 'Video right',
      tags: ['video right', 'text'],
    },
    { id: 'features_double', name: 'Features', tags: ['features'] },
    { id: 'features_triple_center', name: 'Features', tags: ['features'] },
    { id: 'bullet_points', name: 'Bullets', tags: ['bullets', 'list'] },
    {
      id: 'features_triple',
      name: 'Features',
      tags: ['features', 'columns', '3'],
    },
    { id: 'features', name: 'Features', tags: ['features', 'columns', '2'] },
    { id: 'bullet_points_double', name: 'Bullets', tags: ['bullets'] },
    { id: 'features_quarter', name: 'Four features', tags: ['four features'] },
    {
      id: 'team',
      name: 'Team',
      tags: ['team', 'profiles', 'people', 'columns', '3'],
    },
    { id: 'profile', name: 'Profile', tags: ['profile', 'person'] },
    { id: 'title_left', name: 'Title', tags: ['title'] },
    { id: 'title_center', name: 'Title', tags: ['title'] },
    { id: 'quote', name: 'Quote', tags: ['quote'] },
    { id: 'pie_chart', name: 'Chart', tags: ['chart', 'graph', 'text'] },
    { id: 'chart', name: 'Chart', tags: ['chart', 'graph'] },
    {
      id: 'hierarchy_1',
      name: 'Hierarchy',
      tags: ['hierarchy', 'tree', 'structure'],
    },
    { id: 'map_3', name: 'Map & text', tags: ['map & text', 'text'] },
    { id: 'map', name: 'Map', tags: ['map', 'location'] },
    { id: 'map_2', name: 'Map', tags: ['map', 'location'] },
    { id: 'accordeon', name: 'Accordion', tags: ['accordion'] },
    { id: 'faq', name: 'FAQ', tags: ['faq', 'accordion'] },
    { id: 'accordeon_2', name: 'Accordion', tags: ['accordion'] },
    {
      id: 'image_placeholder',
      name: 'Image placeholder',
      tags: [
        'image placeholder',
        'placeholder',
        'picture',
        'illustration',
        'photograph',
      ],
    },
    {
      id: 'text_on_image_2',
      name: 'Text on image',
      tags: ['text on image', 'image'],
    },
    {
      id: 'text_on_image_3',
      name: 'Text on image',
      tags: ['text on image', 'image', 'columns', '2'],
    },
    {
      id: 'title_and_paragraph',
      name: 'Title & Paragraph',
      tags: ['title & paragraph', 'title'],
    },
  ],
  title: '',
}

const NAVIGATION: WireframeSection = {
  id: 'navigation',
  items: [
    { id: 'header_type_2', name: 'Header', tags: ['header', 'navigation'] },
    { id: 'header', name: 'Header', tags: ['header', 'navigation'] },
    { id: 'interface_header', name: 'Header', tags: ['header', 'navigation'] },
    {
      id: 'hamburger',
      name: 'Hamburger',
      tags: ['hamburger', 'menu', 'sandwich', 'navigation'],
    },
    {
      id: 'no_logo_navigation',
      name: 'Navigation',
      tags: ['header', 'navigation'],
    },
    {
      id: 'hamburger_right',
      name: 'Hamburger',
      tags: ['hamburger', 'menu', 'sandwich', 'navigation'],
    },
    {
      id: 'breadcrumbs',
      name: 'Breadcrumbs',
      tags: ['breadcrumbs', 'navigation'],
    },
    { id: 'mobile_top_bar', name: 'Top bar', tags: ['header', 'navigation'] },
    { id: 'tabs', name: 'Tabs', tags: ['tabs', 'navigation'] },
    { id: 'divider', name: 'Divider', tags: ['divider', 'line', 'separator'] },
    { id: 'spacer', name: 'Spacer', tags: ['spacer', 'empty', 'separator'] },
    {
      id: 'divider_dashed',
      name: 'Divider',
      tags: ['divider', 'dashed', 'separator'],
    },
    {
      id: 'back',
      name: 'Previous',
      tags: ['previous', 'arrow', 'back', 'navigation'],
    },
    {
      id: 'pagination',
      name: 'Pagination',
      tags: ['pagination', 'navigation'],
    },
    { id: 'forward', name: 'Next', tags: ['next', 'arrow', 'navigation'] },
    {
      id: 'table_of_contents',
      name: 'Table of contents',
      tags: ['table of contents', 'toc'],
    },
    { id: 'timeline', name: 'Timeline', tags: ['timeline'] },
    {
      id: 'timeline_2',
      name: 'Timeline vertical',
      tags: ['timeline vertical'],
    },
    { id: 'footer_type_2', name: 'Footer', tags: ['footer', 'navigation'] },
    { id: 'footer', name: 'Footer', tags: ['footer', 'navigation'] },
    {
      id: 'mobile_bottom_bar',
      name: 'Bottom bar',
      tags: ['footer', 'socials', 'navigation'],
    },
  ],
  title: 'Navigation',
}

const ACTIONS: WireframeSection = {
  id: 'actions',
  items: [
    { id: 'cta_right', name: 'CTA & Image', tags: ['cta & image', 'image'] },
    { id: 'cta', name: 'CTA', tags: ['cta'] },
    { id: 'cta_left', name: 'CTA & Image', tags: ['cta & image', 'image'] },
    { id: 'text_and_form', name: 'Form left', tags: ['form left', 'text'] },
    { id: 'cta_2', name: 'CTA', tags: ['cta'] },
    { id: 'text_and_form_2', name: 'Form right', tags: ['form right', 'text'] },
    { id: 'form', name: 'Feedback form', tags: ['feedback form', 'review'] },
    {
      id: 'sign_up',
      name: 'Sign In',
      tags: ['sign in', 'login', 'authorization'],
    },
    {
      id: 'text_and_sidebar_form',
      name: 'Small form right',
      tags: ['small form right', 'text'],
    },
    { id: 'form_3', name: 'Form', tags: ['form'] },
    { id: 'steps', name: 'Steps', tags: ['steps', 'progress'] },
    {
      id: 'product_card',
      name: 'Product card',
      tags: ['product card', 'card'],
    },
    { id: 'rating', name: 'Rating', tags: ['rating', 'stars'] },
    { id: 'searchbar', name: 'Search', tags: ['search'] },
    { id: 'newsletter', name: 'Subscribe form', tags: ['subscribe form'] },
    { id: 'dropdown', name: 'Dropdown', tags: ['dropdown'] },
    { id: 'input', name: 'Input', tags: ['input'] },
    { id: 'inputs', name: 'Inputs', tags: ['inputs'] },
    {
      id: 'radio_buttons',
      name: 'Radio buttons',
      tags: ['radio buttons', 'filter'],
    },
    { id: 'todo', name: 'Checklist', tags: ['checklist', 'filter'] },
    { id: 'toggle', name: 'Toggles', tags: ['toggles', 'filter'] },
    { id: 'filter', name: 'Filter', tags: ['filter', 'toggles'] },
    {
      id: 'download',
      name: 'File download',
      tags: ['file download', 'document'],
    },
    { id: 'upload_button', name: 'Upload button', tags: ['upload button'] },
    { id: 'audio_player', name: 'Audio', tags: ['audio'] },
    { id: 'calendar', name: 'Calendar', tags: ['calendar', 'table'] },
    { id: 'pricing', name: 'Plans', tags: ['plans', 'table', 'kanban'] },
    { id: 'left_button', name: 'Button', tags: ['button', 'cta'] },
    { id: 'button', name: 'Button', tags: ['button', 'cta'] },
    { id: 'invoice', name: 'Invoice', tags: ['invoice', 'table'] },
    { id: 'store_buttons_2', name: 'Buttons', tags: ['buttons'] },
    { id: 'store_buttons', name: 'Buttons', tags: ['buttons'] },
    { id: 'right_button', name: 'Button', tags: ['button', 'cta'] },
    { id: 'language', name: 'Language', tags: ['language'] },
    { id: 'error_404', name: '404 error', tags: ['404 error', 'error'] },
    { id: 'qr_code', name: 'QR Code', tags: ['qr code'] },
    { id: 'messengers', name: 'Messengers', tags: ['messengers'] },
    {
      id: 'social_buttons',
      name: 'Social buttons',
      tags: ['social buttons', 'facebook', 'instagram'],
    },
    {
      id: 'app_store_buttons',
      name: 'iOS / Android buttons',
      tags: ['app store buttons', 'android', 'apple', 'download', 'app'],
    },
    { id: 'loading_2', name: 'Loading', tags: ['loading'] },
    { id: 'loading', name: 'Loading', tags: ['loading'] },
  ],
  title: 'Actions',
}

// The catalog of the wireframes menu, in its order; Content opens the list with no heading.
export const WIREFRAME_GROUPS: WireframeGroup[] = [CONTENT, NAVIGATION, ACTIONS]

// Drawable but no longer offered: a project may still carry them.
const HIDDEN_WIREFRAMES: BlockWireframe[] = ['hierarchy_2', 'form_2']

export const FREE_WIREFRAMES: BlockWireframe[] = [
  'wide',
  'text',
  'header',
  'double',
  'text_double',
  'divider_dashed',
  'triple',
  'text_triple',
  'footer',
  'logos',
  'text_quarter',
]

const WIREFRAME_IDS: ReadonlySet<string> = new Set([
  ...WIREFRAME_GROUPS.flatMap((group) => group.items.map((item) => item.id)),
  ...HIDDEN_WIREFRAMES,
])

// Whether an id from outside (an import, a crawler, a model) names a wireframe
// the catalog can draw.
export const isBlockWireframe = (id: string): id is BlockWireframe =>
  WIREFRAME_IDS.has(id)
